import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import SearchIcon from "@mui/icons-material/Search";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import {
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Tabs,
  Tab
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import FilterListIcon from "@mui/icons-material/FilterList";
import Popover from "@mui/material/Popover";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import DialogContent from "@mui/material/DialogContent";
import { Link } from "react-router-dom";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import LoadingComponent from "../loading";
import { kioskModel } from "../Axios/AxiosConfig";

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Stack,
  Chip
} from "@mui/material";
import { format } from "date-fns";

dayjs.extend(isBetween);
dayjs.extend(quarterOfYear);

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: "var(--themeSecondaryColor, #E7E7DB)",
  borderRadius: "10px",
}));

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const getKioskStyles = (name) => {
  if (!name) return { bgcolor: "#f5f5f5", color: "#666" };
  const colors = [
    { bgcolor: "#FBF1E7", color: "#E69E5C" }, // goldorange50 / goldorange300
    { bgcolor: "#DBF9DB", color: "#1C8686" }, // mildgreen / goldorange (teal)
    { bgcolor: "#8CEB8C", color: "#3B362A" }, // ulvalactucagreen / sensaichabrown
    { bgcolor: "#FDFDFD", color: "#1C8686" }, // brilliance / goldorange (teal)
    { bgcolor: "#F4ECD6", color: "#E69E5C" }, // blondecurl200 / goldorange300
    { bgcolor: "#DBF9DB", color: "#75623F" }, // mildgreen / YellowMetal
    { bgcolor: "#FBF1E7", color: "#1C8686" }, // goldorange50 / goldorange (teal)
    { bgcolor: "#E7E7DB", color: "#E69E5C" }, // blondecurl / goldorange300
    { bgcolor: "#8CEB8C", color: "#75623F" }, // ulvalactucagreen / YellowMetal
    { bgcolor: "#F8F2E3", color: "#1C8686" }, // blondecurl300 / goldorange (teal)
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

function PatientList() {
  const [open, setOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [allPatients, setAllPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // "desc" = newest first, "asc" = oldest first
  const [dataScope, setDataScope] = useState(""); // "Today", "Custom Range", "All Time"
  const [dateRange, setDateRangeState] = useState({
    startDate: null,
    endDate: null,
  });
  const [tempRange, setTempRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [activeSelection, setActiveSelection] = useState("start"); // "start" or "end"

  const [columnFilters, setColumnFilters] = useState({
    unselectedGender: [],
    unselectedKiosk: [],
  });
  const [anchorEl, setAnchorEl] = useState({
    gender: null,
    kiosk: null,
  });

  const uniqueKiosks = React.useMemo(() => {
    const kiosks = [...new Set(allPatients.map((p) => p.kiosk_name).filter(Boolean))];
    return kiosks.sort();
  }, [allPatients]);

  const handleFilterClick = (event, type) => {
    setAnchorEl((prev) => ({ ...prev, [type]: event.currentTarget }));
  };

  const handleFilterClose = (type) => {
    setAnchorEl((prev) => ({ ...prev, [type]: null }));
  };

  const toggleColumnFilter = (type, value) => {
    setColumnFilters((prev) => {
      const field = type === "gender" ? "unselectedGender" : "unselectedKiosk";
      const current = prev[field];
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      return { ...prev, [field]: next };
    });
  };



  // Read kiosk_id from cookie (same as Main.jsx)
  const kiosk_detailsCookie = Cookies.get("kiosk_details");
  let kiosk_details = {};
  if (kiosk_detailsCookie) {
    try {
      kiosk_details = JSON.parse(kiosk_detailsCookie);
    } catch (e) {
      console.log("Error parsing kiosk_details cookie:", e);
    }
  }

  // Same fetchData logic as Main.jsx
  const fetchData = async (start = null, end = null, isAllTime = false) => {
    try {
      setLoading(true);
      let res;

      if (isAllTime) {
        // Explicitly fetch all patients
        res = await kioskModel.post("/getAllClinicalPatientsByCompany", {
          kiosk_id: kiosk_details?.kiosk_id,
        });
        if (res.status === 200) {
          setAllPatients(res.data || []);
          setDataScope("All Time");
        }
        setLoading(false);
        return;
      }

      if (start && end) {
        // Fetch specific date range
        res = await kioskModel.post("/getClinicalKioskReportbyDateByCompany", {
          startDate: dayjs(start).format("YYYY-MM-DD"),
          endDate: dayjs(end).format("YYYY-MM-DD"),
          kiosk_id: kiosk_details?.kiosk_id,
        });
        setDataScope("Custom Range");
      } else {
        // Default today's data
        const today = dayjs().format("YYYY-MM-DD");
        res = await kioskModel.post("/getClinicalKioskReportbyDate", {
          date: today,
          kiosk_id: kiosk_details?.kiosk_id,
        });
        setDataScope("Today");
      }

      if (res.status === 200 && res.data.length > 0) {
        // Deduplicate patients by UID so multiple visits by the same person don't create duplicate rows
        const uniquePatientsMap = new Map();
        res.data.forEach((patient) => {
          const id = patient?.UID || patient?.uid || patient?.PID || Math.random();
          if (!uniquePatientsMap.has(id)) {
            uniquePatientsMap.set(id, patient);
          }
        });
        setAllPatients(Array.from(uniquePatientsMap.values()));
      } else {
        // If range search or default today search yields nothing
        setAllPatients([]);
        if (start && end) setDataScope("Custom Range (No results)");
      }
    } catch (err) {
      console.error("Error fetching patient list:", err);
      toast.error("Failed to load patient data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Apply search, column filters, and sort
  useEffect(() => {
    let filtered = [...allPatients];

    // Search filter
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.firstname?.toLowerCase().includes(lower) ||
          p.lastname?.toLowerCase().includes(lower) ||
          p.UID?.toString().includes(lower) ||
          p.kiosk_id?.toString().includes(lower)
      );
    }

    // Tab Filter (0 = All, 1 = Follow-Up, 2 = New)
    if (tabValue === 1) {
      filtered = filtered.filter((p) => {
        const type = (p.consultation_type || p.patient_type || p.visit_type || "").toLowerCase();
        return type.includes("follow") || type === "follow-up";
      });
    } else if (tabValue === 2) {
      filtered = filtered.filter((p) => {
        const type = (p.consultation_type || p.patient_type || p.visit_type || "").toLowerCase();
        return type.includes("new") || type === "first";
      });
    }

    // Column Filters (Gender)
    if (columnFilters.unselectedGender.length > 0) {
      filtered = filtered.filter((p) => !columnFilters.unselectedGender.includes(p.gender));
    }

    // Column Filters (Kiosk)
    if (columnFilters.unselectedKiosk.length > 0) {
      filtered = filtered.filter((p) => !columnFilters.unselectedKiosk.includes(p.kiosk_name));
    }

    // Sort by date — respects sortOrder toggle
    filtered.sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at || 0);
      const dateB = new Date(b.updated_at || b.created_at || 0);
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });

    setFilteredPatients(filtered);
  }, [allPatients, searchTerm, dateRange, columnFilters, sortOrder, tabValue]);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleDateRangeChange = (range) => {
    setDateRangeState({
      startDate: range.startDate,
      endDate: range.endDate,
    });
  };

  const presets = [
    { label: "Today", getValue: () => ({ startDate: dayjs(), endDate: dayjs() }) },
    { label: "Yesterday", getValue: () => ({ startDate: dayjs().subtract(1, "day"), endDate: dayjs().subtract(1, "day") }) },
    { label: "Last 7 Days", getValue: () => ({ startDate: dayjs().subtract(6, "day"), endDate: dayjs() }) },
    { label: "Last 30 Days", getValue: () => ({ startDate: dayjs().subtract(29, "day"), endDate: dayjs() }) },
    { label: "Last Month", getValue: () => ({ startDate: dayjs().subtract(1, "month").startOf("month"), endDate: dayjs().subtract(1, "month").endOf("month") }) },
    { label: "Last Quarter", getValue: () => ({ startDate: dayjs().subtract(1, "quarter").startOf("quarter"), endDate: dayjs().subtract(1, "quarter").endOf("quarter") }) },
    { label: "All Time", getValue: () => ({ startDate: null, endDate: null, isAllTime: true }) },
  ];

  const handlePresetClick = (preset) => {
    const range = preset.getValue();
    setTempRange({ ...range, label: preset.label });
    setActiveSelection("start");
  };

  const handleDateChange = (newValue) => {
    if (activeSelection === "start") {
      // Clear isAllTime when user picks a custom date
      setTempRange((prev) => ({ ...prev, startDate: newValue, label: "Custom", isAllTime: false }));
      setActiveSelection("end"); // Move to end date selection
    } else {
      // Ensure end date is not before start date
      if (newValue.isBefore(tempRange.startDate, "day")) {
        setTempRange({ startDate: newValue, endDate: newValue, label: "Custom", isAllTime: false });
        setActiveSelection("end");
      } else {
        setTempRange((prev) => ({ ...prev, endDate: newValue, label: "Custom", isAllTime: false }));
      }
    }
  };

  const handleApply = () => {
    setDateRangeState(tempRange);
    fetchData(tempRange.startDate, tempRange.endDate, tempRange.isAllTime);
    setOpen(false);
  };

  const handleClearDateRange = () => {
    setTempRange({ startDate: null, endDate: null });
    setDateRangeState({ startDate: null, endDate: null });
    handleClose();
    fetchData();
  };

  function calculateAge(dob) {
    if (!dob) return "";
    let day, month, year;
    if (dob.includes("/")) {
      [day, month, year] = dob.split("/").map((part) => parseInt(part, 10));
    } else if (dob.length === 8) {
      day = parseInt(dob.substring(0, 2), 10);
      month = parseInt(dob.substring(2, 4), 10);
      year = parseInt(dob.substring(4, 8), 10);
    } else {
      return "";
    }
    const dobDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const hasBirthdayPassed =
      today.getMonth() > dobDate.getMonth() ||
      (today.getMonth() === dobDate.getMonth() &&
        today.getDate() >= dobDate.getDate());
    if (!hasBirthdayPassed) age--;
    return `${age} years old`;
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress style={{ color: "#DA6E0B" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: "#F8F8F4", minHeight: "100vh", fontFamily: "'Poppins', sans-serif" }}>
      {/* Header Section */}
      <Box sx={{ mb: 6, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#3B362A", mb: 1, letterSpacing: "-0.5px" }}>
            Patient Directory
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                bgcolor: "#F4ECD6",
                color: "#75623F",
                px: 2,
                py: 0.5,
                borderRadius: "12px",
                fontSize: "0.85rem",
                fontWeight: 700,
                border: "1px solid rgba(117, 98, 63, 0.1)"
              }}
            >
              {filteredPatients.length} Total Patients
            </Box>
            {dataScope && (
              <Chip
                label={dataScope}
                size="small"
                sx={{
                  bgcolor: dataScope === "Today" ? "#DBF9DB" : "#FBF1E7",
                  color: dataScope === "Today" ? "#1C8686" : "#DA6E0B",
                  fontWeight: 800,
                  fontSize: "0.7rem",
                  borderRadius: "8px",
                  border: "none"
                }}
              />
            )}
          </Stack>
        </Box>

        {/* Search Input */}
        <div className="Patientlistinput flex-grow relative">
          <input
            placeholder="Search"
            className="w-full px-3 py-2 border border-[color:var(--themePrimaryColor,#d1d5db)] rounded-md text-sm"
            style={{ paddingRight: "40px" }} // Space for the icon
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="absolute top-1/2 right-3 transform -translate-y-1/2 text-[color:var(--themePrimaryColor,#6B7280)]">
            <SearchIcon />
          </span>
        </div>
        <Button
          className="flex items-center border !border-amber-600 !text-amber-600 rounded px-4 py-2 text-sm font-medium"
          onClick={handleClickOpen}
        >
          <DateRangeIcon className="mr-2" /> Sort by Date
        </Button>
      </Box>

      {/* <div className="flex items-center justify-center">
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          textColor="inherit" // Use "inherit" instead of a hex value
          indicatorColor="primary" // Use "primary" or customize via sx
          sx={{
            margin: "20px 0",
            "& .MuiTabs-indicator": {
              backgroundColor: "var(--themePrimaryColor, #DA6E0B)", // Custom indicator color
            },
            "& .MuiTab-root": {
              color: "var(--themePrimaryColor, #3B362A)", // Default text color
              fontWeight: 500,
              fontFamily: "'Poppins', sans-serif",
            },
            "& .Mui-selected": {
              color: "var(--themePrimaryColor, #DA6E0B) !important", // Text color of active tab
            },
          }}
        >
          <Tab label="All" />
          <Tab label="Follow-Up" />
          <Tab label="New" />
        </Tabs>
      </div> */}

      <div className="list-appointments">
        <TableContainer
          style={{ borderRadius: "8px" }}
          className="AppointmentTable"
        >
          <Table>
            <StyledTableHead>
              <TableRow>
                <TableCell className="tableheadtitle" align="center">
                  Date
                </TableCell>

                <TableCell className="tableheadtitle">Patient Name</TableCell>
                <TableCell className="tableheadtitle" align="center">
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                    Gender
                    <IconButton size="small" onClick={(e) => handleFilterClick(e, "gender")}>
                      <FilterListIcon sx={{ fontSize: 14, color: columnFilters.unselectedGender.length > 0 ? "#DA6E0B" : "inherit" }} />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell align="center">Age</TableCell>
                <TableCell align="center">Unique ID</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                    Kiosk Location
                    <IconButton size="small" onClick={(e) => handleFilterClick(e, "kiosk")}>
                      <FilterListIcon sx={{ fontSize: 14, color: columnFilters.unselectedKiosk.length > 0 ? "#DA6E0B" : "inherit" }} />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ pr: 4 }}>Action</TableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {filteredPatients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 12, border: "none" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", opacity: 0.6 }}>
                      <img src="/icons/NoAppointment.png" alt="No data" style={{ width: 120, marginBottom: 24 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "#3B362A" }}>No records match your search</Typography>
                      <Typography variant="body2" sx={{ color: "#918165" }}>Try adjusting your filters or search keywords</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPatients.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      bgcolor: "white",
                      borderRadius: "16px",
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                        "& .view-profile-btn": { bgcolor: "#DA6E0B", color: "white" }
                      },
                      "& td": { border: "none" },
                      "& td:first-of-type": { borderRadius: "16px 0 0 16px" },
                      "& td:last-of-type": { borderRadius: "0 16px 16px 0" }
                    }}
                  >
                    <TableCell align="center">
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#3B362A" }}>
                        {row.updated_at
                          ? format(new Date(row.updated_at), "dd MMM, yyyy")
                          : (row.created_at ? format(new Date(row.created_at), "dd MMM, yyyy") : "–")}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#918165" }}>
                        {row.updated_at ? format(new Date(row.updated_at), "hh:mm a") : ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "12px",
                            bgcolor: "#F4ECD6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#75623F",
                            fontWeight: 800,
                            fontSize: "1.1rem"
                          }}
                        >
                          {row?.firstname?.charAt(0)}
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: "#3B362A", fontSize: "0.95rem" }}>
                            {row?.firstname} {row?.lastname}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#918165", textTransform: "capitalize" }}>
                            {row?.consultation_type || row?.patient_type || row?.visit_type || "Patient"} Record
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row?.gender}
                        size="small"
                        sx={{
                          bgcolor: row?.gender === "Male" ? "#E3F2FD" : row?.gender === "Female" ? "#FCE4EC" : "#F5F5F5",
                          color: row?.gender === "Male" ? "#1976D2" : row?.gender === "Female" ? "#C2185B" : "#666",
                          fontWeight: 700,
                          fontSize: "0.7rem"
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontWeight: 600, color: "#3B362A", fontSize: "0.85rem" }}>
                        {calculateAge(row?.dob).split(" ")[0]} <span style={{ fontSize: "0.7rem", color: "#918165" }}>YRS</span>
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: "#DA6E0B", fontWeight: 700, bgcolor: "#FBF1E7", px: 1, py: 0.5, borderRadius: "6px", display: "inline-block" }}>
                        {row?.UID || "–"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {row?.kiosk_name ? (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: getKioskStyles(row.kiosk_name).color }} />
                          <Typography sx={{ fontWeight: 600, color: "#3B362A", fontSize: "0.85rem" }}>
                            {row.kiosk_name}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: "#bbb" }}>–</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 4 }}>
                      <Button
                        variant="outlined"
                        className="view-profile-btn"
                        sx={{
                          textTransform: "none",
                          borderRadius: "0",
                          border: "1px solid var(--themePrimaryColor, #DA6E0B) ",
                          color: "var(--themePrimaryColor, #DA6E0B)",
                          boxSizing: "border-box",
                          borderRadius: "5px",
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: 500,
                          padding: "5px",
                        }}
                        component={Link}
                        to={`/Patient/${row.UID}`}
                      >
                        View Profile
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Popovers for filtering */}
      <Popover
        open={Boolean(anchorEl.gender)}
        anchorEl={anchorEl.gender}
        onClose={() => handleFilterClose("gender")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ sx: { p: 2, borderRadius: "16px", boxShadow: "0 10px 40px rgba(0,0,0,0.1)", mt: 1 } }}
      >
        <Typography variant="caption" sx={{ fontWeight: 800, color: "#918165", mb: 2, display: "block", letterSpacing: "1px" }}>FILTER GENDER</Typography>
        <FormGroup>
          {["Male", "Female", "Other"].map((g) => (
            <FormControlLabel
              key={g}
              control={<Checkbox size="small" checked={!columnFilters.unselectedGender.includes(g)} onChange={() => toggleColumnFilter("gender", g)} sx={{ color: "#DA6E0B", "&.Mui-checked": { color: "#DA6E0B" } }} />}
              label={<Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#3B362A" }}>{g}</Typography>}
            />
          ))}
        </FormGroup>
      </Popover>

      <Popover
        open={Boolean(anchorEl.kiosk)}
        anchorEl={anchorEl.kiosk}
        onClose={() => handleFilterClose("kiosk")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ sx: { p: 2, minWidth: 240, borderRadius: "16px", boxShadow: "0 10px 40px rgba(0,0,0,0.1)", mt: 1 } }}
      >
        <Typography variant="caption" sx={{ fontWeight: 800, color: "#918165", mb: 2, display: "block", letterSpacing: "1px" }}>FILTER KIOSK LOCATION</Typography>
        <FormGroup sx={{ maxHeight: 300, overflowY: "auto" }}>
          {uniqueKiosks.map((k) => (
            <FormControlLabel
              key={k}
              control={<Checkbox size="small" checked={!columnFilters.unselectedKiosk.includes(k)} onChange={() => toggleColumnFilter("kiosk", k)} sx={{ color: "#DA6E0B", "&.Mui-checked": { color: "#DA6E0B" } }} />}
              label={<Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#3B362A" }}>{k}</Typography>}
            />
          ))}
        </FormGroup>
      </Popover>

      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Select Date Range
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers sx={{ p: 0 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", minWidth: "600px", minHeight: "400px" }}>
              {/* Presets Column */}
              <Box sx={{ width: "200px", borderRight: "1px solid #E7E7DB", py: 2 }}>
                <List sx={{ p: 0 }}>
                  {presets.map((preset) => (
                    <ListItem key={preset.label} disablePadding>
                      <ListItemButton
                        onClick={() => handlePresetClick(preset)}
                        sx={{
                          py: 1.5,
                          bgcolor: tempRange.label === preset.label ? "#FBF1E7" : "transparent",
                          "&:hover": { bgcolor: "#FBF1E7" }
                        }}
                      >
                        <ListItemText
                          primary={preset.label}
                          primaryTypographyProps={{
                            fontSize: "0.9rem",
                            fontWeight: tempRange.label === preset.label ? 700 : 500,
                            color: tempRange.label === preset.label ? "#DA6E0B" : "#3B362A"
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
                <Box sx={{ mt: "auto", p: 2 }}>
                  <Button
                    onClick={handleClearDateRange}
                    sx={{ color: "#DA6E0B", textTransform: "none", fontWeight: 700 }}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>

              {/* Calendar / Range Display Column */}
              <Box sx={{ flexGrow: 1, p: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
                {/* Selection Toggles */}
                <Box sx={{ display: "flex", gap: 1, mb: 3, bgcolor: "#F1EFEC", p: 0.5, borderRadius: "10px", width: "100%", maxWidth: "320px" }}>
                  <Box
                    onClick={() => setActiveSelection("start")}
                    sx={{
                      flex: 1,
                      textAlign: "center",
                      py: 1,
                      borderRadius: "8px",
                      cursor: "pointer",
                      bgcolor: activeSelection === "start" ? "#FFF" : "transparent",
                      boxShadow: activeSelection === "start" ? "0 2px 8px rgba(0,0,0,0.05)" : "none",
                      transition: "all 0.2s"
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#918165", display: "block", fontWeight: 700 }}>START DATE</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: activeSelection === "start" ? "#DA6E0B" : "#3B362A" }}>
                      {tempRange.startDate ? tempRange.startDate.format("DD MMM YY") : "---"}
                    </Typography>
                  </Box>
                  <Box
                    onClick={() => setActiveSelection("end")}
                    sx={{
                      flex: 1,
                      textAlign: "center",
                      py: 1,
                      borderRadius: "8px",
                      cursor: "pointer",
                      bgcolor: activeSelection === "end" ? "#FFF" : "transparent",
                      boxShadow: activeSelection === "end" ? "0 2px 8px rgba(0,0,0,0.05)" : "none",
                      transition: "all 0.2s"
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#918165", display: "block", fontWeight: 700 }}>END DATE</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: activeSelection === "end" ? "#DA6E0B" : "#3B362A" }}>
                      {tempRange.endDate ? tempRange.endDate.format("DD MMM YY") : "---"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <DateCalendar
                    value={activeSelection === "start" ? (tempRange.startDate || dayjs()) : (tempRange.endDate || dayjs())}
                    onChange={handleDateChange}
                    sx={{
                      border: "1px solid #F1EFEC",
                      borderRadius: "12px",
                      "& .MuiPickersDay-root.Mui-selected": {
                        bgcolor: "#DA6E0B !important"
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearDateRange} color="inherit">
            Clear Filter
          </Button>
          <Button autoFocus onClick={handleApply} sx={{ color: "#DA6E0B" }}>
            Apply
          </Button>
        </DialogActions>
      </BootstrapDialog>
      <style>
        {`
          .search-input-hover:focus {
            border-color: #DA6E0B !important;
            box-shadow: 0 0 0 2px rgba(218, 110, 11, 0.1);
          }
          .TableRowContainer:hover {
            background-color: #F8F8F4 !important;
          }
        `}
      </style>
    </Box>
  );
}

export default PatientList;
